import React, { useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import styles from "./SmartSourceLensKpi.module.css";
import Skeleton from "@mui/material/Skeleton";
import InfoIcon from "../../../../assets/images/InfoIcon.svg";

/* UOM */
const getUomLabel = (uom) => {
  if (!uom) return "";

  const map = {
    tonnes: "T",
    cases: "C",
    eaches: "E",
  };

  return map[uom.toLowerCase()] || "";
};

/* Format */
const formatValue = (val) => {
  if (val === null || val === undefined) return "0";

  return val.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
};

/* Tooltip */
function InfoWithHover({ description = "" }) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState("top");
  const ref = React.useRef(null);

  const handleEnter = () => {
    if (!description || !ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const left = rect.left + rect.width / 2;

    if (rect.top > 100) {
      setPlacement("top");
      setCoords({
        top: rect.top + scrollY - 8,
        left,
      });
    } else {
      setPlacement("bottom");
      setCoords({
        top: rect.bottom + scrollY + 8,
        left,
      });
    }

    setShow(true);
  };

  const handleLeave = () => {
    setShow(false);
  };

  return (
    <>
      <button
        type="button"
        ref={ref}
        className={`${styles.infoWrapper} ${description ? "" : styles.noTooltip}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        aria-label={description || "Information"}
      >
        <img src={InfoIcon} alt="info" width={12} height={12} />
      </button>

      {show && description
        ? createPortal(
            <div
              className={`${styles.tooltipPortal} ${
                placement === "top" ? styles.tooltipTop : styles.tooltipBottom
              }`}
              style={{
                top: coords.top,
                left: coords.left,
              }}
            >
              <div className={styles.tooltipContent}>{description}</div>
              <div className={styles.tooltipArrow} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

InfoWithHover.propTypes = {
  description: PropTypes.string,
};

/* KPI CARD */
const KPICard = ({ title, value = 0, unit = "", description = "", loading = false, type }) => (
  <div className={`${styles.card} ${styles[type]}`}>
    <div className={styles.titleRow}>
      <span>{title}</span>
      <InfoWithHover description={description} />
    </div>

    {loading ? (
      <Skeleton width={100} height={28} />
    ) : (
      <div className={styles.value}>
        <span className={styles.valueBold}>{formatValue(value)}</span>{" "}
        <span className={styles.unit}>{getUomLabel(unit)}</span>
      </div>
    )}
  </div>
);

KPICard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  description: PropTypes.string,
  loading: PropTypes.bool,
  type: PropTypes.string.isRequired,
};

/* LOSS MITIGATION */
const LossMitigationCard = ({ data = {}, loading = false }) => {
  return (
    <div className={`${styles.lossCard} ${styles.lossMitigation}`}>
      <div className={styles.titleRow}>
        <span>
          Loss Mitigation :{" "}
          {!loading && (
            <>
              <span className={styles.valueBold}>
                {formatValue(data?.value)}
              </span>{" "}
              <span className={styles.unit}>{getUomLabel(data?.unit)}</span>
            </>
          )}
        </span>

        <InfoWithHover description={data?.description} />
      </div>

      <div className={styles.breakdownRow}>
        {loading
          ? [0, 1, 2, 3].map((id) => (
              <Skeleton key={`skeleton-${id}`} height={60} />
            ))
          : data?.lossContents?.map((item) => (
              <div key={item.name} className={styles.breakdownItem}>
                <div className={styles.breakdownHeader}>
                  <span>{item.name}</span>
                  <InfoWithHover description={item.description} />
                </div>

                <div className={styles.valueRow}>
                  <span>
                    <span className={styles.valueBold}>
                      {formatValue(item.value)}
                    </span>{" "}
                    <span className={styles.unit}>
                      {getUomLabel(item.unit)}
                    </span>
                  </span>

                  <span className={styles.percentage}>
                    {item.percentage || 0}%
                  </span>
                </div>

                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${item.percentage || 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}
      </div>
    </div>
  );
};

LossMitigationCard.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unit: PropTypes.string,
    description: PropTypes.string,
    lossContents: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unit: PropTypes.string,
        percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
};

/* MAIN */
const SmartSourceLensKpi = ({ kpiData = [], loading = false }) => {
  const data = kpiData?.[0] || {};

  return (
    <div className={styles.container}>
      <KPICard
        title="Order Loss"
        value={data?.["Order Loss"]?.value}
        unit={data?.["Order Loss"]?.unit}
        description={data?.["Order Loss"]?.description}
        loading={loading}
        type="orderLoss"
      />

      <KPICard
        title="NR Loss"
        value={data?.["NR Loss"]?.value}
        unit={data?.["NR Loss"]?.unit}
        description={data?.["NR Loss"]?.description}
        loading={loading}
        type="nrLoss"
      />

      <LossMitigationCard data={data?.["Loss Mitigation"]} loading={loading} />
    </div>
  );
};

SmartSourceLensKpi.propTypes = {
  kpiData: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

export default SmartSourceLensKpi;
